import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import Button from '@material-ui/core/Button';
import ArticleLayout, { ArticleFallback } from '../Common/ArticleLayout';
import { LoadingState, RichText } from '../Common/PageLayout';
import { formatDate } from '../Common/useEntries';
import { descriptionSummary } from './descriptionText';
import { tokens } from '../../theme';
import { formatDeadline, getConsultation, listComments } from './consultationApi';
import CommentForm from './CommentForm';
import CommentThread from './CommentThread';
import { CollapsibleContent } from './ReadMore';

const SITE = 'https://www.mof.gov.tl';

const styles = {
    heading: { fontFamily: tokens.headingFont, color: tokens.blue900, margin: '48px 0 16px' },
    formHeading: { fontFamily: tokens.headingFont, color: tokens.blue900, fontSize: '1.125rem', margin: '0 0 8px' },
    formPanel: {
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        padding: 24,
        marginBottom: 24,
        background: tokens.surface,
    },
};

/** Comments shown on the page: top-level ones, plus replies whose parent is still published. */
function countVisible(comments) {
    const ids = new Set(comments.map((comment) => comment.id));
    return comments.filter((comment) => !comment.parentId || ids.has(comment.parentId)).length;
}

/** A single consultation: the proposal, its documents, approved comments and the comment form. */
export default function ConsultationDetails({ match }) {
    const slug = match?.params?.slug;
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [commentsError, setCommentsError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        getConsultation(slug)
            .then(async (found) => {
                if (!active) return;
                setPost(found);
                setLoading(false);
                if (!found) return;
                try {
                    const approved = await listComments(found.id);
                    if (active) setComments(approved);
                } catch {
                    if (active) setCommentsError(true);
                }
            })
            .catch(() => {
                if (!active) return;
                setPost(null);
                setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [slug]);

    // Comments are published on submission, so reload the list to show the visitor's own comment.
    function refreshComments() {
        listComments(post.id)
            .then((approved) => { setComments(approved); setCommentsError(false); })
            .catch(() => setCommentsError(true));
    }

    if (loading) return <LoadingState label="Loading consultation" />;

    if (!post) {
        return (
            <ArticleFallback
                title="This consultation could not be found"
                description="It may have closed or been removed. Open consultations are listed on the Public consultations page."
            />
        );
    }

    return (
        <>
            <Helmet defer={false}>
                <title>{`${post.title} | Public consultations | Ministry of Finance, Timor-Leste`}</title>
                <meta name="description" content={descriptionSummary(post.description, 160)} />
            </Helmet>

            <ArticleLayout
                eyebrow="Public consultation"
                title={post.title}
                breadcrumbs={[
                    { label: 'Public consultations', to: '/consultations' },
                    { label: post.title },
                ]}
                image={post.cover?.large}
                imageAlt={post.cover?.alt || post.title}
                details={[
                    { label: 'Category', value: post.category },
                    { label: 'Published', value: formatDate(post.publishedAt) },
                    { label: 'Open for comments from', value: formatDeadline(post.startDate) },
                    { label: 'Comments close', value: formatDeadline(post.endDate) },
                    { label: 'Comments', value: String(countVisible(comments)) },
                ]}
                shareUrl={`${SITE}/consultationdetails/${post.slug}`}
                sidebar={post.documents.length > 0 && (
                    <div style={{ marginBottom: 24, display: 'grid', gap: 12 }}>
                        {post.documents.map((file) => (
                            <Button
                                key={file.url}
                                fullWidth
                                variant="contained"
                                color="primary"
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {`Download ${file.name}`}
                            </Button>
                        ))}
                    </div>
                )}
            >
                {/* Long proposals are clipped with a "Read more" button so the comments stay in reach. */}
                <CollapsibleContent label="Read the full proposal">
                    {/* The CMS editor saves HTML; older posts were written in Markdown. */}
                    {/<\/?[a-z][\s\S]*>/i.test(post.description) ? (
                        <RichText html={post.description} />
                    ) : (
                        <div className="mof-rich-text">
                            <ReactMarkdown>{post.description}</ReactMarkdown>
                        </div>
                    )}
                </CollapsibleContent>

                <h2 style={styles.heading}>Comments</h2>
                <div style={styles.formPanel}>
                    <h3 style={styles.formHeading}>Leave a comment</h3>
                    <p style={{ marginTop: 0, color: tokens.inkSoft }}>
                        Your comment and name are published on this page. The Ministry may remove comments that are offensive or off-topic.
                    </p>
                    <CommentForm postId={post.id} onSubmitted={refreshComments} />
                </div>

                {commentsError && <p>Comments could not be loaded right now.</p>}
                {!commentsError && comments.length === 0 && (
                    <p style={{ color: tokens.muted }}>No comments yet. Be the first to share your view.</p>
                )}
                {!commentsError && (
                    <CommentThread comments={comments} postId={post.id} onChanged={refreshComments} />
                )}
            </ArticleLayout>
        </>
    );
}
